import type { TFunction } from 'i18next';

import type {
  BlackbaudRaisersEdgeIntegration,
  IBlackbaudRaisersEdgeIntegrationAttributes,
} from '@feathr/blackbox';
import type {
  IIntegrationUIToggleSection,
  TIntegrationUISettingField,
  TIntegrationUIToggleValues,
} from '@feathr/components';

import {
  CONSTITUENT_DOWNSYNC_KEY,
  DONATION_UPSYNC_KEY,
  FORM_SUBMISSION_UPSYNC_KEY,
  GIFT_DOWNSYNC_KEY,
} from '../raisersEdgeConfig';

type TDownsyncActivities = IBlackbaudRaisersEdgeIntegrationAttributes['downsync_activities'];
type TUpsyncActivities = IBlackbaudRaisersEdgeIntegrationAttributes['upsync_activities'];
/** The upsync activities that are on/off switches. */
type TUpsyncSwitchKey = {
  [K in keyof TUpsyncActivities]-?: NonNullable<TUpsyncActivities[K]> extends boolean ? K : never;
}[keyof TUpsyncActivities];

/** The sync toggles' values from the model. */
export function getActivityValues({
  downsync_activities: downsync,
  upsync_activities: upsync,
}: Partial<
  Pick<IBlackbaudRaisersEdgeIntegrationAttributes, 'downsync_activities' | 'upsync_activities'>
>): TIntegrationUIToggleValues {
  return {
    [CONSTITUENT_DOWNSYNC_KEY]: downsync?.constituents ?? false,
    [DONATION_UPSYNC_KEY]: upsync?.donations ?? false,
    [FORM_SUBMISSION_UPSYNC_KEY]: !!upsync?.people && !!upsync.people_from_form_submissions,
    [GIFT_DOWNSYNC_KEY]: downsync?.gift_events ?? false,
  };
}

/** The downsync activities with one activity set. Keeps the other activities as they are. */
export function setDownsyncActivity(
  downsync: TDownsyncActivities | undefined,
  key: keyof TDownsyncActivities,
  value: boolean,
): TDownsyncActivities {
  return { constituents: false, gift_events: false, ...downsync, [key]: value };
}

/** Stages one downsync activity on the model. The page's Save & Sync persists it. */
export function stageDownsyncActivity(
  integration: BlackbaudRaisersEdgeIntegration,
  key: keyof TDownsyncActivities,
  value: boolean,
): void {
  integration.set({
    downsync_activities: setDownsyncActivity(integration.get('downsync_activities'), key, value),
  });
}

/** Each optional upsync activity's default: off, or every form. */
const OPTIONAL_UPSYNC_DEFAULTS = {
  people: false,
  people_from_form_submissions: false,
  people_form_ids: null,
} as const satisfies Partial<TUpsyncActivities>;

/** The saved upsync activities. A clean attribute has no shadow, so they are the current ones. */
export function getSavedUpsync(
  integration: BlackbaudRaisersEdgeIntegration,
): TUpsyncActivities | undefined {
  return integration.shadowAttributes.upsync_activities ?? integration.get('upsync_activities');
}

/** Stages upsync activities on the model. Keeps the others; the page's Save & Sync persists it. */
export function stageUpsyncActivities(
  integration: BlackbaudRaisersEdgeIntegration,
  activities: Partial<TUpsyncActivities>,
): void {
  const saved = getSavedUpsync(integration);
  const next = { ...integration.get('upsync_activities'), ...activities };
  // The form picker shows only while form submissions is on. Once it is hidden, an empty list
  // would block Save with no picker to fix it, so put back the saved list, or all forms when the
  // saved list is empty too.
  if (!(next.people && next.people_from_form_submissions) && isPeopleFormListEmpty(next)) {
    next.people_form_ids = isPeopleFormListEmpty(saved) ? null : saved?.people_form_ids ?? null;
  }
  // A default the saved object never had stays absent, so going back to it leaves Save clean.
  for (const [key, value] of Object.entries(OPTIONAL_UPSYNC_DEFAULTS)) {
    const optionalKey = key as keyof typeof OPTIONAL_UPSYNC_DEFAULTS;
    if (next[optionalKey] === value && saved?.[optionalKey] === undefined) {
      delete next[optionalKey];
    }
  }
  integration.set({ upsync_activities: next });
}

/** Stages one upsync switch on the model. Keeps the others; the page's Save & Sync persists it. */
export function stageUpsyncActivity(
  integration: BlackbaudRaisersEdgeIntegration,
  key: TUpsyncSwitchKey,
  value: boolean,
): void {
  stageUpsyncActivities(integration, { [key]: value });
}

/**
 * Stages the forms whose submissions create constituents: `null` for every form, or a list of
 * form IDs. An empty list means "Select specific forms" with no form chosen yet, and blocks Save.
 */
export function stagePeopleFormIds(
  integration: BlackbaudRaisersEdgeIntegration,
  formIds: string[] | null,
): void {
  stageUpsyncActivities(integration, { people_form_ids: formIds });
}

/** Whether "Select specific forms" is staged with no form chosen. Magpie rejects an empty list. */
export function isPeopleFormListEmpty(upsync: TUpsyncActivities | undefined): boolean {
  const formIds = upsync?.people_form_ids;
  return Array.isArray(formIds) && !formIds.length;
}

/** What the picker and Save & Sync say while `isPeopleFormListEmpty` is true. */
export function getPeopleFormListEmptyMessage(t: TFunction): string {
  return t('Select at least one form, or choose All forms.');
}

/** Whether donation upsync is on with no fund to put the donations in. */
export function isFundMissing({
  fundraising,
  upsync_activities: upsync,
}: Partial<
  Pick<IBlackbaudRaisersEdgeIntegrationAttributes, 'fundraising' | 'upsync_activities'>
>): boolean {
  return !!upsync?.donations && !fundraising?.fund_id;
}

/** The field a toggle shows while it is on, if the sections declare one. */
export function getToggleField(
  sections: readonly IIntegrationUIToggleSection[],
  key: string,
): TIntegrationUISettingField | undefined {
  return sections.flatMap((section) => section.toggles).find((toggle) => toggle.key === key)?.field;
}

/** The sections with one toggle disabled for `reason`. */
export function disableToggle(
  sections: readonly IIntegrationUIToggleSection[],
  key: string,
  reason: string,
): IIntegrationUIToggleSection[] {
  return sections.map((section) => ({
    ...section,
    toggles: section.toggles.map((toggle) =>
      toggle.key === key ? { ...toggle, disabledReason: reason } : toggle,
    ),
  }));
}
